import { useState, useEffect } from "react";
import axios from "axios";
import {
  ClipboardCheck,
  User,
  Star,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Minus,
  FileText,
  Target,
  TrendingUp,
  Shield,
  Calendar,
  Download,
  Send,
  Loader2,
  ChevronRight,
  RefreshCw,
  Briefcase,
  GraduationCap,
  Users,
  Brain,
  MessageSquare,
  Sparkles,
  ArrowLeft,
  Mail,
  Clock,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Slider } from "./ui/slider";
import { Progress } from "./ui/progress";
import { Separator } from "./ui/separator";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Score display component
const ScoreSlider = ({ label, value, onChange, icon: Icon }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="flex items-center gap-2 text-sm">
        <Icon className="w-4 h-4 text-muted-foreground" />
        {label}
      </Label>
      <span className="font-semibold text-primary">{value}/5</span>
    </div>
    <Slider
      value={[value]}
      onValueChange={([v]) => onChange(v)}
      min={1}
      max={5}
      step={1}
      className="w-full"
    />
    <div className="flex justify-between text-xs text-muted-foreground">
      <span>Bajo</span>
      <span>Alto</span>
    </div>
  </div>
);

// CV Validation item component
const CVValidationItem = ({ category, label, icon: Icon, value, onChange, observation, onObservationChange }) => {
  const alignmentOptions = [
    { value: "matches", label: "Coincide", icon: CheckCircle2, color: "text-green-600" },
    { value: "partial", label: "Parcial", icon: Minus, color: "text-amber-600" },
    { value: "no_match", label: "No coincide", icon: XCircle, color: "text-red-600" },
  ];

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-primary" />
        <span className="font-medium">{label}</span>
      </div>
      
      <RadioGroup
        value={value.alignment}
        onValueChange={(alignment) => onChange({ ...value, alignment })}
        className="flex gap-4"
      >
        {alignmentOptions.map((option) => (
          <div key={option.value} className="flex items-center space-x-2">
            <RadioGroupItem value={option.value} id={`${category}-${option.value}`} />
            <Label
              htmlFor={`${category}-${option.value}`}
              className={`flex items-center gap-1 cursor-pointer ${value.alignment === option.value ? option.color : ""}`}
            >
              <option.icon className="w-4 h-4" />
              {option.label}
            </Label>
          </div>
        ))}
      </RadioGroup>

      {value.alignment !== "matches" && (
        <Textarea
          placeholder="Observación sobre la discrepancia..."
          value={observation}
          onChange={(e) => onObservationChange(e.target.value)}
          className="mt-2 text-sm"
          rows={2}
        />
      )}
    </div>
  );
};

// Tag input for observations
const TagInput = ({ tags, setTags, placeholder, maxTags = 5 }) => {
  const [input, setInput] = useState("");

  const addTag = () => {
    if (input.trim() && tags.length < maxTags && !tags.includes(input.trim())) {
      setTags([...tags, input.trim()]);
      setInput("");
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <Button type="button" size="sm" variant="outline" onClick={addTag} disabled={tags.length >= maxTags}>
          Agregar
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag, idx) => (
          <Badge key={idx} variant="secondary" className="gap-1">
            {tag}
            <button onClick={() => removeTag(tag)} className="ml-1 hover:text-destructive">
              ×
            </button>
          </Badge>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{tags.length}/{maxTags} máximo</p>
    </div>
  );
};

export default function PostInterviewEvaluation({ 
  candidate, 
  job, 
  onClose, 
  onEvaluationComplete,
  existingEvaluation = null 
}) {
  const [loading, setLoading] = useState(false);
  // Start at step 4 (results) if there's an existing evaluation, otherwise step 1
  const [step, setStep] = useState(existingEvaluation ? 4 : 1);
  
  // Form state
  const [scores, setScores] = useState({
    communication: existingEvaluation?.scores?.communication || 3,
    technical_domain: existingEvaluation?.scores?.technical_domain || 3,
    cultural_fit: existingEvaluation?.scores?.cultural_fit || 3,
    learning_capacity: existingEvaluation?.scores?.learning_capacity || 3,
  });

  const [cvValidation, setCvValidation] = useState({
    technical_skills: { 
      alignment: existingEvaluation?.cv_validation?.find(v => v.category === "technical_skills")?.alignment || "matches",
      cv_claim: `Habilidades: ${candidate?.data?.skills?.slice(0, 5).join(", ") || "N/A"}`,
      observation: existingEvaluation?.cv_validation?.find(v => v.category === "technical_skills")?.observation || ""
    },
    experience: { 
      alignment: existingEvaluation?.cv_validation?.find(v => v.category === "experience")?.alignment || "matches",
      cv_claim: `${candidate?.data?.years_experience || 0} años de experiencia`,
      observation: existingEvaluation?.cv_validation?.find(v => v.category === "experience")?.observation || ""
    },
    education: { 
      alignment: existingEvaluation?.cv_validation?.find(v => v.category === "education")?.alignment || "matches",
      cv_claim: candidate?.data?.education?.[0] || "No especificada",
      observation: existingEvaluation?.cv_validation?.find(v => v.category === "education")?.observation || ""
    },
    soft_skills: { 
      alignment: existingEvaluation?.cv_validation?.find(v => v.category === "soft_skills")?.alignment || "matches",
      cv_claim: "Habilidades blandas según CV",
      observation: existingEvaluation?.cv_validation?.find(v => v.category === "soft_skills")?.observation || ""
    },
  });

  const [observations, setObservations] = useState({
    strengths: existingEvaluation?.observations?.strengths || [],
    gaps: existingEvaluation?.observations?.gaps || [],
    risks: existingEvaluation?.observations?.risks || [],
  });

  const [decision, setDecision] = useState(existingEvaluation?.preliminary_decision || "");
  const [additionalNotes, setAdditionalNotes] = useState(existingEvaluation?.additional_notes || "");
  const [extendedDevelopment, setExtendedDevelopment] = useState(existingEvaluation?.extended_development_needed || false);

  // Email dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Result state
  const [evaluationResult, setEvaluationResult] = useState(existingEvaluation || null);

  const handleSubmit = async () => {
    if (!decision) {
      toast.error("Por favor selecciona una decisión preliminar");
      return;
    }

    setLoading(true);
    try {
      const cvValidationArray = Object.entries(cvValidation).map(([category, data]) => ({
        category,
        cv_claim: data.cv_claim,
        alignment: data.alignment,
        observation: data.observation || null,
      }));

      const payload = {
        candidate_id: candidate.id,
        job_id: job.id,
        scores,
        cv_validation: cvValidationArray,
        observations,
        preliminary_decision: decision,
        additional_notes: additionalNotes || null,
        extended_development_needed: extendedDevelopment,
      };

      const endpoint = existingEvaluation 
        ? `${API}/jobs/${job.id}/candidates/${candidate.id}/evaluation`
        : `${API}/jobs/${job.id}/candidates/${candidate.id}/evaluate-interview`;
      
      const method = existingEvaluation ? "put" : "post";
      
      const response = await axios[method](endpoint, payload);
      
      setEvaluationResult(response.data.evaluation || response.data);
      setStep(4); // Go to results step
      
      toast.success(existingEvaluation ? "Evaluación actualizada" : "Evaluación guardada exitosamente");
      
      if (onEvaluationComplete) {
        onEvaluationComplete(response.data);
      }
    } catch (error) {
      console.error("Error saving evaluation:", error);
      toast.error(error.response?.data?.detail || "Error al guardar la evaluación");
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailRecipients.trim()) {
      toast.error("Ingresa al menos un email");
      return;
    }

    // Parse emails (comma or newline separated)
    const emails = emailRecipients
      .split(/[,\n]/)
      .map(e => e.trim())
      .filter(e => e && e.includes("@"));

    if (emails.length === 0) {
      toast.error("No se encontraron emails válidos");
      return;
    }

    setSendingEmail(true);
    try {
      await axios.post(
        `${API}/jobs/${job.id}/candidates/${candidate.id}/send-evaluation-email`,
        {
          recipient_emails: emails,
          include_plan: true,
          include_observations: true
        }
      );

      toast.success(`Evaluación enviada a ${emails.length} destinatario(s)`);
      setShowEmailDialog(false);
      setEmailRecipients("");
      
      // Update result to show email was sent
      setEvaluationResult(prev => ({
        ...prev,
        plan_sent_at: new Date().toISOString(),
        plan_sent_to: emails
      }));
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.response?.data?.detail || "Error al enviar email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleRegeneratePlan = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/jobs/${job.id}/candidates/${candidate.id}/regenerate-plan`
      );
      
      setEvaluationResult(prev => ({
        ...prev,
        development_plan: response.data.development_plan
      }));
      
      toast.success("Plan de desarrollo regenerado");
    } catch (error) {
      console.error("Error regenerating plan:", error);
      toast.error(error.response?.data?.detail || "Error al regenerar el plan");
    } finally {
      setLoading(false);
    }
  };

  const downloadPlanPDF = () => {
    // Create a simple text version for now - could be enhanced with jsPDF
    const plan = evaluationResult?.development_plan;
    if (!plan) return;

    const planType = plan.extended_plan ? "EXTENDIDO" : "30-60-90 DÍAS";
    const totalDays = plan.total_duration_days || 90;

    const content = `
╔══════════════════════════════════════════════════════════════════╗
║           PLAN DE DESARROLLO ${planType}                         
╚══════════════════════════════════════════════════════════════════╝

INFORMACIÓN GENERAL
═══════════════════
Candidato: ${candidate?.data?.name || "N/A"}
Puesto: ${job?.title || "N/A"}
Fecha de generación: ${new Date().toLocaleDateString("es-PA")}
Duración total: ${totalDays} días
Seguimiento recomendado: ${plan.recommended_check_ins || "Semanal"}

${evaluationResult?.extended_development_needed ? "⚠️ ATENCIÓN: Este candidato requiere desarrollo EXTENDIDO (>90 días)\n" : ""}

RESUMEN EJECUTIVO
═════════════════
${plan.summary || ""}

${plan.key_focus_areas?.length > 0 ? `
ÁREAS DE ENFOQUE PRINCIPAL
══════════════════════════
${plan.key_focus_areas.map(a => `• ${a}`).join("\n")}
` : ""}

FASES DEL PLAN
══════════════
${plan.phases?.map(phase => `
┌─────────────────────────────────────────
│ ${phase.phase.toUpperCase()}
│ Enfoque: ${phase.focus}
└─────────────────────────────────────────

Objetivos:
${phase.objectives?.map(o => `  ✓ ${o}`).join("\n") || "  - Sin objetivos definidos"}

Acciones:
${phase.actions?.map(a => `  • [${a.priority?.toUpperCase()}] ${a.action} (${a.type})${a.responsible ? ` - Responsable: ${a.responsible}` : ""}`).join("\n") || "  - Sin acciones definidas"}

Métricas de éxito:
${phase.success_metrics?.map(m => `  ◆ ${m}`).join("\n") || "  - Sin métricas definidas"}

${phase.checkpoint ? `Checkpoint: ${phase.checkpoint}` : ""}
`).join("\n") || ""}

RECURSOS NECESARIOS
═══════════════════
${plan.key_support_needed?.map(r => `• ${r}`).join("\n") || "- No especificados"}

MITIGACIÓN DE RIESGOS
═════════════════════
${plan.risk_mitigation?.map(r => `• ${r}`).join("\n") || "- No especificados"}

${plan.success_criteria ? `
CRITERIO DE ÉXITO GENERAL
═════════════════════════
${plan.success_criteria}
` : ""}

──────────────────────────────────────────
Generado por: ${plan.generated_by || "Sistema"}
Fecha: ${plan.generated_at ? new Date(plan.generated_at).toLocaleString("es-PA") : new Date().toLocaleString("es-PA")}
──────────────────────────────────────────
    `.trim();

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plan-desarrollo-${totalDays}dias-${candidate?.data?.name?.replace(/\s+/g, "-") || "candidato"}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Plan descargado");
  };

  // Calculate alignment preview
  const calculateAlignmentPreview = () => {
    const weights = { technical_skills: 0.35, experience: 0.30, education: 0.15, soft_skills: 0.20 };
    const scores = { matches: 100, partial: 60, no_match: 20 };
    
    let total = 0;
    Object.entries(cvValidation).forEach(([cat, data]) => {
      total += weights[cat] * scores[data.alignment];
    });
    
    return Math.round(total);
  };

  const getAlignmentColor = (value) => {
    if (value >= 90) return "text-green-600";
    if (value >= 70) return "text-amber-600";
    return "text-red-600";
  };

  const getDecisionConfig = (dec) => {
    const configs = {
      hire: { label: "Contratar", icon: CheckCircle2, color: "bg-green-100 text-green-800 border-green-200" },
      hire_with_plan: { label: "Contratar con Plan", icon: FileText, color: "bg-blue-100 text-blue-800 border-blue-200" },
      no_hire: { label: "No Contratar", icon: XCircle, color: "bg-red-100 text-red-800 border-red-200" },
    };
    return configs[dec] || { label: dec, icon: Minus, color: "bg-gray-100" };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <ClipboardCheck className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold">
              Evaluación Post-Entrevista
            </h2>
            <p className="text-muted-foreground text-sm">
              {candidate?.data?.name || "Candidato"} • {job?.title || "Puesto"}
            </p>
          </div>
        </div>
        {step < 4 && (
          <Badge variant="outline">
            Paso {step} de 3
          </Badge>
        )}
      </div>

      {/* Progress */}
      {step < 4 && (
        <div className="flex gap-2">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-2 flex-1 rounded-full transition-colors ${
                s <= step ? "bg-primary" : "bg-muted"
              }`}
            />
          ))}
        </div>
      )}

      {/* Step 1: Quantitative Evaluation */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5" />
              Evaluación Cuantitativa
            </CardTitle>
            <CardDescription>
              Califica cada aspecto del 1 al 5 según lo observado en la entrevista
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <ScoreSlider
              label="Comunicación"
              value={scores.communication}
              onChange={(v) => setScores({ ...scores, communication: v })}
              icon={MessageSquare}
            />
            <ScoreSlider
              label="Dominio Técnico"
              value={scores.technical_domain}
              onChange={(v) => setScores({ ...scores, technical_domain: v })}
              icon={Brain}
            />
            <ScoreSlider
              label="Actitud / Fit Cultural"
              value={scores.cultural_fit}
              onChange={(v) => setScores({ ...scores, cultural_fit: v })}
              icon={Users}
            />
            <ScoreSlider
              label="Capacidad de Aprendizaje"
              value={scores.learning_capacity}
              onChange={(v) => setScores({ ...scores, learning_capacity: v })}
              icon={TrendingUp}
            />

            <div className="flex justify-end pt-4">
              <Button onClick={() => setStep(2)}>
                Continuar
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: CV Validation */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Validación del CV
            </CardTitle>
            <CardDescription>
              Compara lo declarado en el CV vs lo observado en la entrevista
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CVValidationItem
              category="technical_skills"
              label="Habilidades Técnicas"
              icon={Brain}
              value={cvValidation.technical_skills}
              onChange={(v) => setCvValidation({ ...cvValidation, technical_skills: v })}
              observation={cvValidation.technical_skills.observation}
              onObservationChange={(obs) => setCvValidation({ 
                ...cvValidation, 
                technical_skills: { ...cvValidation.technical_skills, observation: obs } 
              })}
            />
            
            <CVValidationItem
              category="experience"
              label="Experiencia Laboral"
              icon={Briefcase}
              value={cvValidation.experience}
              onChange={(v) => setCvValidation({ ...cvValidation, experience: v })}
              observation={cvValidation.experience.observation}
              onObservationChange={(obs) => setCvValidation({ 
                ...cvValidation, 
                experience: { ...cvValidation.experience, observation: obs } 
              })}
            />
            
            <CVValidationItem
              category="education"
              label="Educación"
              icon={GraduationCap}
              value={cvValidation.education}
              onChange={(v) => setCvValidation({ ...cvValidation, education: v })}
              observation={cvValidation.education.observation}
              onObservationChange={(obs) => setCvValidation({ 
                ...cvValidation, 
                education: { ...cvValidation.education, observation: obs } 
              })}
            />
            
            <CVValidationItem
              category="soft_skills"
              label="Habilidades Blandas"
              icon={Users}
              value={cvValidation.soft_skills}
              onChange={(v) => setCvValidation({ ...cvValidation, soft_skills: v })}
              observation={cvValidation.soft_skills.observation}
              onObservationChange={(obs) => setCvValidation({ 
                ...cvValidation, 
                soft_skills: { ...cvValidation.soft_skills, observation: obs } 
              })}
            />

            {/* Alignment Preview */}
            <div className="bg-muted/50 rounded-lg p-4 mt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Índice de Alineación CV-Persona</span>
                <span className={`text-2xl font-bold ${getAlignmentColor(calculateAlignmentPreview())}`}>
                  {calculateAlignmentPreview()}%
                </span>
              </div>
              <Progress value={calculateAlignmentPreview()} className="mt-2" />
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>
              <Button onClick={() => setStep(3)}>
                Continuar
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Observations & Decision */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Observaciones y Decisión
            </CardTitle>
            <CardDescription>
              Registra las observaciones clave y tu decisión preliminar
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Strengths */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Fortalezas Observadas
              </Label>
              <TagInput
                tags={observations.strengths}
                setTags={(tags) => setObservations({ ...observations, strengths: tags })}
                placeholder="Ej: Excelente comunicación técnica"
              />
            </div>

            {/* Gaps */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                Brechas Observadas
              </Label>
              <TagInput
                tags={observations.gaps}
                setTags={(tags) => setObservations({ ...observations, gaps: tags })}
                placeholder="Ej: Falta experiencia en liderazgo"
              />
            </div>

            {/* Risks */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-red-600" />
                Riesgos Percibidos
              </Label>
              <TagInput
                tags={observations.risks}
                setTags={(tags) => setObservations({ ...observations, risks: tags })}
                placeholder="Ej: Posible rotación temprana"
                maxTags={3}
              />
            </div>

            <Separator />

            {/* Decision */}
            <div>
              <Label className="mb-3 block font-semibold">Decisión Preliminar</Label>
              <RadioGroup
                value={decision}
                onValueChange={setDecision}
                className="grid gap-3"
              >
                <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${decision === "hire" ? "border-green-500 bg-green-50" : "border-muted hover:border-green-200"}`}>
                  <RadioGroupItem value="hire" id="hire" />
                  <Label htmlFor="hire" className="flex items-center gap-2 cursor-pointer flex-1">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <div>
                      <span className="font-medium">Contratar</span>
                      <p className="text-sm text-muted-foreground">El candidato cumple con los requisitos</p>
                    </div>
                  </Label>
                </div>
                
                <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${decision === "hire_with_plan" ? "border-blue-500 bg-blue-50" : "border-muted hover:border-blue-200"}`}>
                  <RadioGroupItem value="hire_with_plan" id="hire_with_plan" />
                  <Label htmlFor="hire_with_plan" className="flex items-center gap-2 cursor-pointer flex-1">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <span className="font-medium">Contratar con Plan de Desarrollo</span>
                      <p className="text-sm text-muted-foreground">Requiere plan 30-60-90 días para cerrar brechas</p>
                    </div>
                  </Label>
                </div>
                
                <div className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${decision === "no_hire" ? "border-red-500 bg-red-50" : "border-muted hover:border-red-200"}`}>
                  <RadioGroupItem value="no_hire" id="no_hire" />
                  <Label htmlFor="no_hire" className="flex items-center gap-2 cursor-pointer flex-1">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <div>
                      <span className="font-medium">No Contratar</span>
                      <p className="text-sm text-muted-foreground">El candidato no cumple con los requisitos</p>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Extended Development Checkbox - only show for hire_with_plan */}
            {decision === "hire_with_plan" && (
              <div className="flex items-start space-x-3 p-4 rounded-lg border-2 border-amber-200 bg-amber-50">
                <Checkbox
                  id="extended-dev"
                  checked={extendedDevelopment}
                  onCheckedChange={setExtendedDevelopment}
                />
                <div className="grid gap-1.5 leading-none">
                  <Label
                    htmlFor="extended-dev"
                    className="flex items-center gap-2 font-medium cursor-pointer"
                  >
                    <Clock className="w-4 h-4 text-amber-600" />
                    Requiere desarrollo extendido (&gt;90 días)
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Marca esta opción si las brechas identificadas requieren más de 90 días para cerrarse
                  </p>
                </div>
              </div>
            )}

            {/* Additional Notes */}
            <div>
              <Label className="mb-2 block">Notas Adicionales (Opcional)</Label>
              <Textarea
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Cualquier comentario adicional relevante..."
                rows={3}
              />
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>
              <Button onClick={handleSubmit} disabled={!decision || loading}>
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Guardar Evaluación
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Results */}
      {step === 4 && evaluationResult && (
        <div className="space-y-6">
          {/* Summary Card */}
          <Card className="border-2 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Evaluación Consolidada
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Decision Badge */}
              <div className="flex flex-col items-center gap-3">
                {(() => {
                  const config = getDecisionConfig(evaluationResult.preliminary_decision);
                  return (
                    <Badge className={`text-lg px-6 py-2 ${config.color}`}>
                      <config.icon className="w-5 h-5 mr-2" />
                      {config.label}
                    </Badge>
                  );
                })()}
                
                {/* Extended Development Warning */}
                {evaluationResult.extended_development_needed && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 border border-amber-300 rounded-lg">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">
                      Requiere desarrollo extendido (&gt;90 días)
                    </span>
                  </div>
                )}
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Fit Técnico</p>
                  <p className="text-2xl font-bold text-primary">{evaluationResult.technical_fit || 0}%</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Fit Conductual</p>
                  <p className="text-2xl font-bold text-primary">{evaluationResult.behavioral_fit || 0}%</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Fit Desarrollo</p>
                  <p className="text-2xl font-bold text-primary">{evaluationResult.development_fit || 0}%</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Alineación CV</p>
                  <p className={`text-2xl font-bold ${getAlignmentColor(evaluationResult.cv_alignment_index)}`}>
                    {evaluationResult.cv_alignment_index}%
                  </p>
                </div>
              </div>

              {/* Score Comparison */}
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Score Original (CV)</span>
                  <span className="font-medium">{candidate?.match_score?.overall_score || 0}%</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Score Ajustado (Post-Entrevista)</span>
                  <span className="font-bold text-primary">{evaluationResult.adjusted_match_score}%</span>
                </div>
                <Progress value={evaluationResult.adjusted_match_score} className="h-3" />
              </div>

              {/* Risk Level */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <span className="font-medium">Nivel de Riesgo</span>
                <Badge variant={
                  evaluationResult.risk_level === "low" ? "default" :
                  evaluationResult.risk_level === "medium" ? "secondary" : "destructive"
                }>
                  {evaluationResult.risk_level === "low" ? "Bajo" :
                   evaluationResult.risk_level === "medium" ? "Medio" : "Alto"}
                </Badge>
              </div>

              {/* Final Recommendation */}
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <p className="text-sm font-medium text-primary mb-1">Recomendación Final</p>
                <p className="text-muted-foreground">{evaluationResult.final_recommendation}</p>
              </div>
            </CardContent>
          </Card>

          {/* Discrepancies */}
          {evaluationResult.discrepancies?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="w-5 h-5" />
                  Discrepancias CV vs Entrevista
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {evaluationResult.discrepancies.map((disc, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline">{disc.category.replace("_", " ").toUpperCase()}</Badge>
                        <Badge variant={disc.impact === "high" ? "destructive" : "secondary"}>
                          Impacto {disc.impact === "high" ? "Alto" : "Medio"}
                        </Badge>
                      </div>
                      <p className="text-sm"><strong>CV:</strong> {disc.cv_claim}</p>
                      <p className="text-sm"><strong>Entrevista:</strong> {disc.interview_result === "partial" ? "Parcialmente coincide" : "No coincide"}</p>
                      {disc.observation && (
                        <p className="text-sm text-muted-foreground mt-1">{disc.observation}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Development Plan */}
          {evaluationResult.development_plan && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Plan de Desarrollo {evaluationResult.development_plan.extended_plan ? "Extendido" : "90 Días"}
                  </CardTitle>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={handleRegeneratePlan} disabled={loading}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                      Regenerar
                    </Button>
                    <Button size="sm" variant="outline" onClick={downloadPlanPDF}>
                      <Download className="w-4 h-4 mr-2" />
                      Descargar
                    </Button>
                    <Button size="sm" onClick={() => setShowEmailDialog(true)}>
                      <Mail className="w-4 h-4 mr-2" />
                      Enviar por Email
                    </Button>
                  </div>
                </div>
                <CardDescription className="text-base mt-2">
                  {evaluationResult.development_plan.summary}
                </CardDescription>
                
                {/* Plan metadata */}
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge variant="outline" className="text-xs">
                    <Clock className="w-3 h-3 mr-1" />
                    {evaluationResult.development_plan.total_duration_days || 90} días
                  </Badge>
                  {evaluationResult.development_plan.extended_plan && (
                    <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">
                      Plan Extendido
                    </Badge>
                  )}
                </div>

                {/* Primary gaps addressed */}
                {evaluationResult.development_plan.primary_gaps_addressed?.length > 0 && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs font-medium text-red-800 mb-2">Brechas a Cerrar con este Plan:</p>
                    <div className="flex flex-wrap gap-1">
                      {evaluationResult.development_plan.primary_gaps_addressed.map((gap, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs border-red-300 text-red-700">
                          {gap}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Email sent indicator */}
                {evaluationResult.plan_sent_at && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>
                      Enviado el {new Date(evaluationResult.plan_sent_at).toLocaleDateString("es-PA")} a {evaluationResult.plan_sent_to?.join(", ")}
                    </span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Supervisor Guide - NEW */}
                {evaluationResult.development_plan.supervisor_guide && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="font-semibold text-blue-900 flex items-center gap-2 mb-3">
                      <Briefcase className="w-4 h-4" />
                      Guía para el Supervisor
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-blue-800 mb-1">Inversión de tiempo semanal:</p>
                        <p className="text-blue-700">{evaluationResult.development_plan.supervisor_guide.weekly_time_investment}</p>
                      </div>
                      {evaluationResult.development_plan.supervisor_guide.key_conversations?.length > 0 && (
                        <div>
                          <p className="font-medium text-blue-800 mb-1">Conversaciones clave:</p>
                          <ul className="text-blue-700 space-y-1">
                            {evaluationResult.development_plan.supervisor_guide.key_conversations.slice(0, 3).map((conv, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <span className="text-blue-500 mt-1">•</span>
                                {conv}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    {evaluationResult.development_plan.supervisor_guide.escalation_triggers?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-blue-300">
                        <p className="font-medium text-red-700 mb-1">⚠️ Escalar a HR si:</p>
                        <ul className="text-red-600 text-sm space-y-1">
                          {evaluationResult.development_plan.supervisor_guide.escalation_triggers.map((trigger, i) => (
                            <li key={i}>• {trigger}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Phases Accordion */}
                <div>
                  <p className="font-semibold mb-3">Fases del Plan</p>
                  <Accordion type="single" collapsible className="w-full">
                    {evaluationResult.development_plan.phases?.map((phase, idx) => (
                      <AccordionItem key={idx} value={`phase-${idx}`}>
                        <AccordionTrigger className="hover:bg-muted/50 px-2 rounded">
                          <div className="flex items-center gap-2 text-left">
                            <Badge variant="outline" className="shrink-0">{phase.phase}</Badge>
                            <span className="text-muted-foreground">{phase.theme || phase.focus}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="space-y-4 pt-4 px-2">
                          {/* Objectives */}
                          <div>
                            <p className="font-medium mb-2 text-primary">Objetivos de esta fase:</p>
                            <ul className="space-y-1">
                              {phase.objectives?.map((obj, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                  <Target className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                                  <span>{obj}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Actions - Enhanced */}
                          <div>
                            <p className="font-medium mb-2 text-primary">Acciones específicas:</p>
                            <div className="space-y-3">
                              {phase.actions?.map((action, i) => (
                                <div key={i} className="p-3 bg-muted/30 rounded-lg border-l-4 border-l-primary">
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <Badge variant={
                                      action.priority === "critical" ? "destructive" :
                                      action.priority === "high" ? "default" : "secondary"
                                    } className="text-xs">
                                      {action.priority === "critical" ? "CRÍTICO" : action.priority === "high" ? "Alta" : "Media"}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs">{action.type}</Badge>
                                    {action.responsible && (
                                      <Badge variant="outline" className="text-xs bg-blue-50">
                                        <User className="w-3 h-3 mr-1" />
                                        {action.responsible}
                                      </Badge>
                                    )}
                                    {action.duration && (
                                      <Badge variant="outline" className="text-xs bg-gray-50">
                                        <Clock className="w-3 h-3 mr-1" />
                                        {action.duration}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm leading-relaxed">{action.action}</p>
                                  {action.deliverable && (
                                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                      <FileText className="w-3 h-3" />
                                      <strong>Entregable:</strong> {action.deliverable}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Success Metrics */}
                          <div>
                            <p className="font-medium mb-2 text-green-700">Métricas de éxito (cuantificables):</p>
                            <ul className="space-y-1">
                              {phase.success_metrics?.map((metric, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                                  <span>{metric}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Weekly Checkpoints - NEW */}
                          {phase.weekly_checkpoints?.length > 0 && (
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="font-medium mb-2 text-gray-700">Checkpoints semanales:</p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                {phase.weekly_checkpoints.map((checkpoint, i) => (
                                  <div key={i} className="p-2 bg-white rounded border">
                                    <span className="font-medium text-gray-600">{checkpoint.split(':')[0]}:</span>
                                    <p className="text-gray-500">{checkpoint.split(':').slice(1).join(':')}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Red Flags - NEW */}
                          {phase.red_flags?.length > 0 && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                              <p className="font-medium mb-2 text-red-700 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                Señales de alerta:
                              </p>
                              <ul className="space-y-1 text-sm text-red-600">
                                {phase.red_flags.map((flag, i) => (
                                  <li key={i}>• {flag}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>

                {/* Resources Required - Enhanced */}
                {evaluationResult.development_plan.resources_required?.length > 0 && (
                  <div className="p-4 border rounded-lg">
                    <p className="font-semibold mb-3 flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-primary" />
                      Recursos Necesarios
                    </p>
                    <div className="space-y-2">
                      {evaluationResult.development_plan.resources_required.map((resource, idx) => (
                        <div key={idx} className="flex flex-wrap items-center gap-2 text-sm p-2 bg-muted/30 rounded">
                          <Badge variant="outline" className="text-xs">{resource.type}</Badge>
                          <span className="font-medium">{resource.resource}</span>
                          {resource.cost_estimate && (
                            <Badge variant="secondary" className="text-xs">{resource.cost_estimate}</Badge>
                          )}
                          {resource.where_to_get && (
                            <span className="text-muted-foreground text-xs">→ {resource.where_to_get}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Risk Mitigation Plan - Enhanced */}
                {evaluationResult.development_plan.risk_mitigation_plan?.length > 0 && (
                  <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                    <p className="font-semibold mb-3 flex items-center gap-2 text-amber-800">
                      <Shield className="w-4 h-4" />
                      Plan de Mitigación de Riesgos
                    </p>
                    <div className="space-y-3">
                      {evaluationResult.development_plan.risk_mitigation_plan.map((item, idx) => (
                        <div key={idx} className="text-sm p-3 bg-white rounded border border-amber-200">
                          <p className="font-medium text-amber-900">⚠️ Riesgo: {item.risk}</p>
                          <p className="text-amber-700 mt-1"><strong>Acción:</strong> {item.mitigation_action}</p>
                          {item.early_warning_signs && (
                            <p className="text-amber-600 mt-1 text-xs"><strong>Detectar temprano:</strong> {item.early_warning_signs}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Success Criteria at End */}
                {(evaluationResult.development_plan.success_criteria_day_90 || evaluationResult.development_plan.success_criteria_day_180 || evaluationResult.development_plan.success_criteria) && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="font-semibold text-green-800 flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4" />
                      Criterio de Éxito al Finalizar el Plan
                    </p>
                    <p className="text-sm text-green-700">
                      {evaluationResult.development_plan.success_criteria_day_90 || 
                       evaluationResult.development_plan.success_criteria_day_180 || 
                       evaluationResult.development_plan.success_criteria}
                    </p>
                  </div>
                )}

                {/* Contingency Plan - NEW */}
                {evaluationResult.development_plan.contingency_if_not_progressing && (
                  <div className="p-4 bg-gray-100 border rounded-lg">
                    <p className="font-semibold mb-2 text-gray-700">
                      Plan de Contingencia (si no hay progreso al día 45):
                    </p>
                    <p className="text-sm text-gray-600">{evaluationResult.development_plan.contingency_if_not_progressing}</p>
                  </div>
                )}

                {/* Check-ins Recommendation */}
                {evaluationResult.development_plan.recommended_check_ins && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                    <span className="font-medium text-blue-800">📅 Frecuencia de seguimiento recomendada:</span>{" "}
                    <span className="text-blue-700">{evaluationResult.development_plan.recommended_check_ins}</span>
                  </div>
                )}

              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>
              Editar Evaluación
            </Button>
            <Button onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>
      )}

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Enviar Evaluación por Email
            </DialogTitle>
            <DialogDescription>
              Envía el reporte completo de evaluación y plan de desarrollo al equipo de contratación.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">
                Destinatarios (separados por coma o salto de línea)
              </Label>
              <Textarea
                value={emailRecipients}
                onChange={(e) => setEmailRecipients(e.target.value)}
                placeholder="reclutador@empresa.com&#10;hiring.manager@empresa.com"
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                El email incluirá la evaluación completa con observaciones y el plan de desarrollo.
              </p>
            </div>
            
            {evaluationResult?.extended_development_needed && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-800">
                  El email incluirá la advertencia de desarrollo extendido (&gt;90 días)
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail}>
              {sendingEmail ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Enviar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
